import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistorique } from '../lib/historique'
import { supabase } from '../lib/supabase'
import { useAuth, peutEcrire, estAdmin } from '../lib/auth'
import { fileDAttente, mettreEnAttente } from '../lib/db'
import { synchroniser, surChangementFile } from '../lib/sync'
import { preparerImage, dateExif } from '../lib/images'
import { dateLongue, horodatageCourt, jourCalendrier } from '../lib/format'
import {
  actionsPossibles,
  ajouterCommentaire,
  aujourdhui,
  changementsStatut,
  classeStatut,
  codeSuivi,
  commentairesLocaux,
  contactsDuChantier,
  creerSuivi,
  enRetard,
  equipe,
  LIBELLE_STATUT,
  lierPhoto,
  lireSuivi,
  modifierSuivi,
  nomAssigne,
  photosDuSuivi,
  rafraichirCommentaires,
  retirerPhoto,
  SANS_CONTACT,
  SANS_EPINGLE,
  TYPES,
  type ActionStatut,
  type AssignationExterne,
  type ContactExterne
} from '../lib/suivis'
import { fichePdf, type ResultatPdf } from '../lib/fichePdf'
import type { Chantier, CommentaireLocal, EpingleSuivi, PhotoSuivi, Profil, SuiviLocal, TypeSuivi } from '../lib/types'
import { CameraRafale } from './CameraRafale'
import { VisionneusePhoto } from '../components/VisionneusePhoto'

type Membre = Pick<Profil, 'id' | 'nom' | 'role'>

/**
 * Fiche d'un suivi (tâche, déficience ou QRT) : consultation, modification,
 * changements de statut, photos et commentaires. Avec `nouveau`, la même
 * fiche sert à la création.
 */
export function FicheSuivi({
  chantier,
  suiviId,
  nouveau,
  fermer,
  retour = '‹ Suivis',
  voirSurPlan
}: {
  chantier: Chantier
  suiviId?: string
  /** Création : type de la sous-liste affichée, et photo déjà rattachée s'il y a lieu. */
  nouveau?: { type: TypeSuivi; photo?: string; epingle?: EpingleSuivi }
  fermer: () => void
  /** Libellé du bouton de retour (« ‹ Plan » quand la fiche s'ouvre sur un plan). */
  retour?: string
  /** « Voir sur le plan » : absent quand la fiche est déjà ouverte sur le plan. */
  voirSurPlan?: (s: SuiviLocal) => void
}) {
  const { profil } = useAuth()
  const [suivi, setSuivi] = useState<SuiviLocal | null>(null)
  const [commentaires, setCommentaires] = useState<CommentaireLocal[]>([])
  const [photos, setPhotos] = useState<PhotoSuivi[]>([])
  const [membres, setMembres] = useState<Membre[]>([])
  const [contacts, setContacts] = useState<ContactExterne[]>([])
  const [envoi, setEnvoi] = useState(false)
  const [edition, setEdition] = useState(!!nouveau)
  const [dialogue, setDialogue] = useState<ActionStatut | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  const [texte, setTexte] = useState('')
  const [confirmerSuppression, setConfirmerSuppression] = useState(false)

  const type: TypeSuivi = suivi?.type ?? nouveau?.type ?? 'tache'
  const infoType = TYPES.find((t) => t.cle === type)!
  const admin = estAdmin(profil)
  const ecrivable = peutEcrire(profil, chantier.statut)
  const peutAgir =
    ecrivable || (!!suivi && !!profil && suivi.assigne_a === profil.id && chantier.statut !== 'termine')
  const noms = useMemo(() => new Map(membres.map((m) => [m.id, m.nom])), [membres])
  const nom = (id: string | null) => (id ? noms.get(id) ?? '—' : '—')

  const recharger = useCallback(async () => {
    if (!suiviId) return
    const s = await lireSuivi(suiviId)
    if (s) setSuivi(s)
    setCommentaires(await commentairesLocaux(suiviId))
    setPhotos(await photosDuSuivi(suiviId))
  }, [suiviId])

  useEffect(() => {
    void equipe().then(setMembres)
    void contactsDuChantier(chantier.id).then(setContacts)
    if (!suiviId) return
    void recharger()
    void rafraichirCommentaires(suiviId).then(setCommentaires)
  }, [suiviId, recharger, chantier.id])

  // Après une synchronisation (numéro reçu, envoi terminé) : relire la fiche.
  useEffect(() => surChangementFile(() => void recharger()), [recharger])

  // Échap ferme (sauf dans un dialogue).
  useEffect(() => {
    function touche(e: KeyboardEvent) {
      if (e.key === 'Escape' && !dialogue) fermer()
    }
    window.addEventListener('keydown', touche)
    return () => window.removeEventListener('keydown', touche)
  }, [fermer, dialogue])

  async function apresChangement() {
    await recharger()
    void synchroniser()
  }

  /* --- changement de statut -------------------------------------- */
  async function appliquer(a: ActionStatut, extra: Record<string, string | null> = {}) {
    if (!suivi || !profil) return
    setOccupe(true)
    setErreur(null)
    try {
      const avant = suivi.statut
      const c = { ...changementsStatut(suivi, a.vers, profil.id), ...extra }
      if (a.cle === 'regler' || a.cle === 'valider') c.motif_rejet = null
      await modifierSuivi(suivi.id, c)
      let journal =
        avant === a.vers
          ? a.libelle
          : `Statut : ${LIBELLE_STATUT[avant] ?? avant} → ${LIBELLE_STATUT[a.vers] ?? a.vers}`
      if (a.besoin === 'motif' && extra.motif_rejet) journal += ` — motif : ${extra.motif_rejet}`
      if (a.besoin === 'transmission')
        journal += ` — à ${extra.destinataire || '—'}, le ${jourCalendrier(extra.transmise_le ?? '')}`
      if (a.besoin === 'reponse') journal += ` — réponse du ${jourCalendrier(extra.repondue_le ?? '')}`
      await ajouterCommentaire(suivi.id, profil.id, journal, 'statut')
      setDialogue(null)
      await apresChangement()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setOccupe(false)
    }
  }

  /* --- commentaire ------------------------------------------------- */
  async function commenter() {
    if (!suivi || !profil || !texte.trim()) return
    await ajouterCommentaire(suivi.id, profil.id, texte.trim())
    setTexte('')
    await apresChangement()
  }

  /* --- suppression (douce) ---------------------------------------- */
  async function supprimer() {
    if (!suivi) return
    await modifierSuivi(suivi.id, { supprime_le: new Date().toISOString() })
    void synchroniser()
    fermer()
  }

  /* --- photos ------------------------------------------------------ */
  async function ajouterPhotos(uuids: string[], id = suivi?.id) {
    if (!id) return
    for (const u of uuids) await lierPhoto(id, u)
    await apresChangement()
  }

  const enCreation = !!nouveau && !suivi
  const s = suivi
  const actions = s && peutAgir && !s.supprime_le ? actionsPossibles(s, admin) : []

  return (
    <div className="feuille">
      <header className="entete">
        <div className="entete-rang">
          <button className="retour" onClick={fermer}>
            {retour}
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {enCreation ? `Nouvelle ${infoType.singulier}` : s ? codeSuivi(s) : '…'}
            </h1>
            <p className="sous">
              {enCreation
                ? chantier.numero
                : s
                  ? `${infoType.singulier} · créée le ${horodatageCourt(s.cree_le)} par ${nom(s.cree_par)}`
                  : ''}
            </p>
          </div>
          <button className="fermer-x" onClick={fermer} aria-label="Fermer" title="Fermer">
            ✕
          </button>
        </div>
      </header>

      {enCreation || (edition && s) ? (
        <FormulaireSuivi
          type={type}
          initial={s}
          membres={membres}
          contacts={contacts}
          photoInitiale={nouveau?.photo}
          epingle={enCreation ? nouveau?.epingle?.epingle_libelle ?? null : null}
          chantier={chantier}
          annuler={() => (enCreation ? fermer() : setEdition(false))}
          enregistrer={async (champs, photosAjoutees) => {
            if (!profil) return
            if (enCreation) {
              const cree = await creerSuivi(
                { chantier_id: chantier.id, type, ...champs, ...(nouveau?.epingle ?? SANS_EPINGLE) },
                profil.id
              )
              for (const u of photosAjoutees) await lierPhoto(cree.id, u)
              void synchroniser()
              fermer()
            } else if (s) {
              if (champs.assigne_a !== s.assigne_a || (champs.assigne_contact_id ?? null) !== (s.assigne_contact_id ?? null))
                await ajouterCommentaire(
                  s.id,
                  profil.id,
                  champs.assigne_a
                    ? `Assignée à ${nom(champs.assigne_a)}`
                    : champs.assigne_contact_nom
                      ? `Assignée à ${champs.assigne_contact_nom} (externe)`
                      : 'Assignation retirée',
                  'assignation'
                )
              await modifierSuivi(s.id, champs)
              setEdition(false)
              await apresChangement()
            }
          }}
        />
      ) : !s ? (
        <div className="corps">
          <p className="vide">Chargement…</p>
        </div>
      ) : (
        <>
          <div className="corps">
            <div className="entre" style={{ marginBottom: 8 }}>
              <h2 style={{ fontSize: 18, letterSpacing: 0, lineHeight: 1.3 }}>{s.titre}</h2>
            </div>
            <div className="jetons" style={{ marginTop: 0, marginBottom: 12 }}>
              <span className={`pastille ${classeStatut(s)}`}>{LIBELLE_STATUT[s.statut] ?? s.statut}</span>
              {enRetard(s) && <span className="pastille p-alerte">En retard</span>}
              {s.etat !== 'ok' && <span className="pastille p-neutre">En file — partira à la reconnexion</span>}
            </div>

            {s.motif_rejet && s.statut !== 'validee' && (
              <p className="avis" style={{ marginBottom: 12 }}>
                <strong>Retournée — motif :</strong> {s.motif_rejet}
              </p>
            )}
            {s.type === 'deficience' && s.statut === 'reglee' && (
              <p className="avis" style={{ marginBottom: 12 }}>
                Marquée réglée par {nom(s.reglee_par)}
                {s.reglee_le ? ` le ${dateLongue(s.reglee_le)}` : ''}.{' '}
                {admin ? 'À valider ou à rejeter.' : 'En attente de validation par un administrateur.'}
              </p>
            )}

            <div className="carte bande-nulle">
              <Ligne libelle="Assignée à" valeur={nomAssigne(s, noms) ?? '—'} />
              {s.assigne_contact_nom && (
                <Ligne libelle="Courriel" valeur={s.assigne_contact_courriel || 'aucun courriel au contact'} />
              )}
              {s.envoye_externe_le && <Ligne libelle="Fiche envoyée" valeur={horodatageCourt(s.envoye_externe_le)} />}
              <Ligne
                libelle="Échéance"
                valeur={s.echeance ? jourCalendrier(s.echeance) : '—'}
                alerte={enRetard(s)}
              />
              {s.emplacement && <Ligne libelle="Emplacement" valeur={s.emplacement} />}
              {s.type === 'qrt' && (
                <>
                  <Ligne libelle="Destinataire" valeur={s.destinataire || '—'} />
                  <Ligne libelle="Transmise le" valeur={s.transmise_le ? jourCalendrier(s.transmise_le) : '—'} />
                </>
              )}
              {s.type === 'deficience' && s.statut === 'validee' && (
                <Ligne
                  libelle="Validée"
                  valeur={`${nom(s.validee_par)}${s.validee_le ? ', ' + dateLongue(s.validee_le) : ''}`}
                />
              )}
            </div>

            {s.epingle_document_id && s.epingle_page != null && (
              <div className="carte bande-nulle" style={{ marginTop: 8 }}>
                <Ligne libelle="Épinglée sur" valeur={s.epingle_libelle ?? `page ${s.epingle_page}`} />
                {(voirSurPlan || ecrivable) && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    {voirSurPlan && (
                      <button className="bouton creux" onClick={() => voirSurPlan(s)}>
                        Voir sur le plan
                      </button>
                    )}
                    {ecrivable && (
                      <button
                        className="bouton creux"
                        onClick={() =>
                          void (async () => {
                            await modifierSuivi(s.id, SANS_EPINGLE)
                            if (profil) await ajouterCommentaire(s.id, profil.id, 'Épingle retirée du plan', 'epingle')
                            await apresChangement()
                          })()
                        }
                      >
                        Retirer l'épingle
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {s.description && (
              <>
                <div className="sect">Description</div>
                <p style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{s.description}</p>
              </>
            )}

            {s.type === 'qrt' && s.reponse && (
              <>
                <div className="sect">
                  Réponse <span className="code">{s.repondue_le ? jourCalendrier(s.repondue_le) : ''}</span>
                </div>
                <p className="carte bande-nulle" style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>
                  {s.reponse}
                </p>
              </>
            )}

            <PhotosSuivi
              chantier={chantier}
              photos={photos}
              modifiable={peutAgir}
              ajouter={(uuids) => void ajouterPhotos(uuids)}
              retirer={(u) => void retirerPhoto(s.id, u).then(apresChangement)}
            />

            <div className="sect">Activité</div>
            <div className="liste">
              {commentaires.length === 0 && <p className="code">Aucun commentaire.</p>}
              {commentaires.map((c) => (
                <div key={c.id} className={`commentaire ${c.evenement ? 'evenement' : ''}`}>
                  <p className="meta" style={{ marginTop: 0 }}>
                    <strong style={{ color: 'var(--ink2)' }}>{nom(c.auteur_id)}</strong>
                    <span>{horodatageCourt(c.cree_le)}</span>
                    {c.etat !== 'ok' && <span>· en file</span>}
                  </p>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{c.texte}</p>
                </div>
              ))}
            </div>
            {peutAgir && (
              <div style={{ marginTop: 10 }}>
                <label className="champ">
                  <span>Ajouter un commentaire</span>
                  <textarea rows={2} value={texte} onChange={(e) => setTexte(e.target.value)} />
                </label>
                <button className="bouton creux" disabled={!texte.trim()} onClick={() => void commenter()}>
                  Publier le commentaire
                </button>
              </div>
            )}

            {(ecrivable || peutAgir) && (
              <div style={{ display: 'flex', gap: 8, marginTop: 22, flexWrap: 'wrap' }}>
                {ecrivable && (
                  <button className="bouton creux" onClick={() => setEdition(true)}>
                    Modifier la fiche
                  </button>
                )}
                {ecrivable && s.assigne_contact_nom && (
                  <button className="bouton creux" onClick={() => setEnvoi(true)}>
                    Envoyer à {s.assigne_contact_nom}
                  </button>
                )}
                {ecrivable && (admin || s.cree_par === profil?.id) && (
                  <button className="bouton creux" onClick={() => setConfirmerSuppression(true)}>
                    Supprimer
                  </button>
                )}
              </div>
            )}

            {erreur && <p className="avis erreur" style={{ marginTop: 12 }}>{erreur}</p>}
            {s.derniere_erreur && s.etat !== 'ok' && (
              <p className="avis erreur" style={{ marginTop: 12 }}>
                Envoi en échec : {s.derniere_erreur}
              </p>
            )}
          </div>

          {actions.length > 0 && (
            <div className="actions" style={{ flexWrap: 'wrap' }}>
              {actions.map((a) => (
                <button
                  key={a.cle}
                  className={`bouton ${a.principale ? '' : 'creux'}`}
                  disabled={occupe}
                  onClick={() => (a.besoin ? setDialogue(a) : void appliquer(a))}
                >
                  {a.libelle}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {dialogue && s && (
        <DialogueStatut
          action={dialogue}
          suivi={s}
          annuler={() => setDialogue(null)}
          valider={(extra) => void appliquer(dialogue, extra)}
        />
      )}

      {envoi && s && profil && (
        <DialogueEnvoi
          suivi={s}
          chantier={chantier}
          auteur={profil.nom}
          annuler={() => setEnvoi(false)}
          envoye={async (moyen) => {
            await modifierSuivi(s.id, { envoye_externe_le: new Date().toISOString() })
            await ajouterCommentaire(
              s.id,
              profil.id,
              `Fiche envoyée en PDF à ${s.assigne_contact_nom}${s.assigne_contact_courriel ? ' <' + s.assigne_contact_courriel + '>' : ''} (${moyen})`,
              'envoi'
            )
            await apresChangement()
          }}
        />
      )}

      {confirmerSuppression && s && (
        <div className="annot-voile" onClick={() => setConfirmerSuppression(false)}>
          <div className="annot-dialogue" onClick={(e) => e.stopPropagation()}>
            <p className="titre">Supprimer {codeSuivi(s)} ?</p>
            <p style={{ fontSize: 13.5 }}>
              La fiche disparaît des listes. Elle reste en base (traçabilité) et un administrateur peut la
              récupérer au besoin.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="bouton creux" onClick={() => setConfirmerSuppression(false)}>
                Annuler
              </button>
              <button className="bouton" onClick={() => void supprimer()}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Ligne({ libelle, valeur, alerte }: { libelle: string; valeur: string; alerte?: boolean }) {
  return (
    <p className="ligne-fiche">
      <span>{libelle}</span>
      <strong className={alerte ? 'retard' : ''}>{valeur}</strong>
    </p>
  )
}

/* ---------------------------------------------------------------------
   Formulaire (création et modification)
   --------------------------------------------------------------------- */

interface ChampsSuivi extends AssignationExterne {
  titre: string
  description: string | null
  emplacement: string | null
  assigne_a: string | null
  echeance: string | null
  destinataire: string | null
}

function FormulaireSuivi({
  type,
  initial,
  membres,
  contacts,
  photoInitiale,
  epingle,
  chantier,
  annuler,
  enregistrer
}: {
  type: TypeSuivi
  initial: SuiviLocal | null
  membres: Membre[]
  contacts: ContactExterne[]
  photoInitiale?: string
  epingle?: string | null
  chantier: Chantier
  annuler: () => void
  enregistrer: (champs: ChampsSuivi, photos: string[]) => Promise<void>
}) {
  const [titre, setTitre] = useState(initial?.titre ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [emplacement, setEmplacement] = useState(initial?.emplacement ?? '')
  // « p:<profil> » = membre de l'équipe ; « c:<contact> » = contact externe du chantier.
  const [assigne, setAssigne] = useState(
    initial?.assigne_a ? `p:${initial.assigne_a}` : initial?.assigne_contact_id ? `c:${initial.assigne_contact_id}` : ''
  )
  // Contact assigné qui n'existe plus au chantier : on garde l'assignation telle quelle.
  const contactDisparu =
    !!initial?.assigne_contact_nom && !contacts.some((c) => c.id === initial.assigne_contact_id)
  const [echeance, setEcheance] = useState(initial?.echeance ?? '')
  const [destinataire, setDestinataire] = useState(initial?.destinataire ?? '')
  const [photos, setPhotos] = useState<string[]>(photoInitiale ? [photoInitiale] : [])
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const exemples: Record<TypeSuivi, string> = {
    tache: 'Ex. : Installer le pare-air au mur nord, niveau 3',
    deficience: 'Ex. : Membrane décollée à la jonction de la dalle',
    qrt: "Ex. : Quelle composition s'applique au retour de mur à l'axe D ?"
  }

  async function valider() {
    if (!titre.trim()) {
      setErreur('Le titre est obligatoire.')
      return
    }
    setOccupe(true)
    setErreur(null)
    let externe: AssignationExterne = SANS_CONTACT
    if (assigne.startsWith('c:')) {
      const c = contacts.find((x) => `c:${x.id}` === assigne)
      externe = c
        ? { assigne_contact_id: c.id, assigne_contact_nom: c.nom, assigne_contact_courriel: c.courriel }
        : {
            assigne_contact_id: initial?.assigne_contact_id ?? null,
            assigne_contact_nom: initial?.assigne_contact_nom ?? null,
            assigne_contact_courriel: initial?.assigne_contact_courriel ?? null
          }
    }
    try {
      await enregistrer(
        {
          ...externe,
          titre: titre.trim(),
          description: description.trim() || null,
          emplacement: emplacement.trim() || null,
          assigne_a: assigne.startsWith('p:') ? assigne.slice(2) : null,
          echeance: echeance || null,
          destinataire: type === 'qrt' ? destinataire.trim() || null : initial?.destinataire ?? null
        },
        photos
      )
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
      setOccupe(false)
    }
  }

  return (
    <>
      <div className="corps">
        {epingle && (
          <p className="avis" style={{ marginBottom: 12 }}>
            Épinglée sur le plan <strong>{epingle}</strong>.
          </p>
        )}
        <label className="champ">
          <span>Titre *</span>
          <input value={titre} onChange={(e) => setTitre(e.target.value)} placeholder={exemples[type]} autoFocus={!initial} />
        </label>
        <label className="champ">
          <span>{type === 'qrt' ? 'Question (détails)' : 'Description'}</span>
          <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className="champ">
          <span>Emplacement (étage, façade, axe…)</span>
          <input value={emplacement} onChange={(e) => setEmplacement(e.target.value)} placeholder="Ex. : Niveau 4, façade ouest, axe C-D" />
        </label>
        <label className="champ">
          <span>Assignée à</span>
          <select value={assigne} onChange={(e) => setAssigne(e.target.value)}>
            <option value="">— Personne —</option>
            <optgroup label="Équipe Isolation Larente">
              {membres.map((m) => (
                <option key={m.id} value={`p:${m.id}`}>
                  {m.nom}
                </option>
              ))}
            </optgroup>
            {(contacts.length > 0 || contactDisparu) && (
              <optgroup label="Contacts du chantier (externes)">
                {contacts.map((c) => (
                  <option key={c.id} value={`c:${c.id}`}>
                    {c.nom}
                    {c.titre ? ` — ${c.titre}` : ''}
                    {c.courriel ? '' : ' (sans courriel)'}
                  </option>
                ))}
                {contactDisparu && initial?.assigne_contact_id && (
                  <option value={`c:${initial.assigne_contact_id}`}>{initial.assigne_contact_nom} (retiré du chantier)</option>
                )}
              </optgroup>
            )}
          </select>
          {contacts.length === 0 && (
            <span className="code" style={{ display: 'block', marginTop: 4 }}>
              Pour assigner à l'architecte ou à l'entrepreneur, un administrateur l'ajoute d'abord aux contacts du chantier (onglet Info).
            </span>
          )}
          {assigne.startsWith('c:') && (
            <span className="code" style={{ display: 'block', marginTop: 4 }}>
              Le contact n'a pas de compte : après l'enregistrement, utilise « Envoyer à… » sur la fiche pour lui transmettre le PDF.
            </span>
          )}
        </label>
        <label className="champ">
          <span>Échéance</span>
          <input type="date" value={echeance} min={initial ? undefined : aujourdhui()} onChange={(e) => setEcheance(e.target.value)} />
        </label>
        {type === 'qrt' && (
          <label className="champ">
            <span>Destinataire (architecte, entrepreneur…)</span>
            <input value={destinataire} onChange={(e) => setDestinataire(e.target.value)} />
          </label>
        )}

        {!initial && (
          <PhotosSuivi
            chantier={chantier}
            photos={photos.map((u) => ({
              cle: u,
              suivi_id: '',
              photo_client_uuid: u,
              moment: 'reference' as const,
              ajoute_le: '',
              etat: 'a_envoyer' as const
            }))}
            modifiable
            ajouter={(uuids) => setPhotos((p) => [...p, ...uuids.filter((u) => !p.includes(u))])}
            retirer={(u) => setPhotos((p) => p.filter((x) => x !== u))}
          />
        )}

        {erreur && <p className="avis erreur" style={{ marginTop: 12 }}>{erreur}</p>}
      </div>
      <div className="actions">
        <button className="bouton creux" onClick={annuler} disabled={occupe}>
          Annuler
        </button>
        <button className="bouton" onClick={() => void valider()} disabled={occupe}>
          {occupe ? 'Enregistrement…' : initial ? 'Enregistrer' : 'Créer'}
        </button>
      </div>
    </>
  )
}

/* ---------------------------------------------------------------------
   Dialogue de changement de statut (motif, transmission, réponse)
   --------------------------------------------------------------------- */

function DialogueStatut({
  action,
  suivi,
  annuler,
  valider
}: {
  action: ActionStatut
  suivi: SuiviLocal
  annuler: () => void
  valider: (extra: Record<string, string | null>) => void
}) {
  const [motif, setMotif] = useState('')
  const [destinataire, setDestinataire] = useState(suivi.destinataire ?? '')
  const [date, setDate] = useState(
    action.besoin === 'reponse' ? suivi.repondue_le ?? aujourdhui() : suivi.transmise_le ?? aujourdhui()
  )
  const [reponse, setReponse] = useState(suivi.reponse ?? '')

  const pret =
    action.besoin === 'motif'
      ? motif.trim().length > 0
      : action.besoin === 'transmission'
        ? destinataire.trim().length > 0 && !!date
        : reponse.trim().length > 0 && !!date

  function ok() {
    if (!pret) return
    if (action.besoin === 'motif') valider({ motif_rejet: motif.trim() })
    else if (action.besoin === 'transmission') valider({ destinataire: destinataire.trim(), transmise_le: date })
    else valider({ reponse: reponse.trim(), repondue_le: date })
  }

  return (
    <div className="annot-voile" onClick={annuler}>
      <div className="annot-dialogue" onClick={(e) => e.stopPropagation()}>
        <p className="titre">{action.libelle}</p>
        {action.besoin === 'motif' && (
          <label className="champ">
            <span>{action.cle === 'rejeter' ? 'Motif du rejet (retourne à la personne assignée)' : 'Raison de la réouverture'}</span>
            <textarea rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} autoFocus />
          </label>
        )}
        {action.besoin === 'transmission' && (
          <>
            <p style={{ fontSize: 13 }}>
              La QRT est transmise hors de l'application (courriel, plateforme de l'entrepreneur). Indique à qui
              et quand : c'est la date qui comptera si la réponse tarde.
            </p>
            <label className="champ">
              <span>Destinataire</span>
              <input value={destinataire} onChange={(e) => setDestinataire(e.target.value)} autoFocus />
            </label>
            <label className="champ">
              <span>Transmise le</span>
              <input type="date" value={date} max={aujourdhui()} onChange={(e) => setDate(e.target.value)} />
            </label>
          </>
        )}
        {action.besoin === 'reponse' && (
          <>
            <label className="champ">
              <span>Réponse reçue</span>
              <textarea rows={5} value={reponse} onChange={(e) => setReponse(e.target.value)} autoFocus />
            </label>
            <label className="champ">
              <span>Reçue le</span>
              <input type="date" value={date} max={aujourdhui()} onChange={(e) => setDate(e.target.value)} />
            </label>
          </>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="bouton creux" onClick={annuler}>
            Annuler
          </button>
          <button className="bouton" disabled={!pret} onClick={ok}>
            Confirmer
          </button>
        </div>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------------
   Envoi de la fiche en PDF à un contact externe
   --------------------------------------------------------------------- */

/**
 * Deux temps : on prépare le PDF, puis on l'envoie d'un deuxième toucher
 * (le partage du téléphone exige un geste récent de l'utilisateur).
 * Téléphone : partage vers Gmail / Outlook avec le PDF en pièce jointe.
 * Ordinateur : le PDF est téléchargé et un nouveau courriel s'ouvre ; il
 * reste à y glisser le fichier.
 */
function DialogueEnvoi({
  suivi,
  chantier,
  auteur,
  annuler,
  envoye
}: {
  suivi: SuiviLocal
  chantier: Chantier
  auteur: string
  annuler: () => void
  envoye: (moyen: string) => Promise<void>
}) {
  const [pdf, setPdf] = useState<ResultatPdf | null>(null)
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [fait, setFait] = useState<string | null>(null)
  const code = codeSuivi(suivi)
  const sujet = `${code} — ${suivi.titre} — ${chantier.numero} ${chantier.nom}`
  const corps =
    `Bonjour ${suivi.assigne_contact_nom ?? ''},\n\n` +
    `Vous trouverez ci-joint la fiche ${code} (${TYPES.find((t) => t.cle === suivi.type)?.singulier}) ` +
    `du chantier ${chantier.numero} — ${chantier.nom}` +
    (suivi.echeance ? `, avec une échéance au ${jourCalendrier(suivi.echeance)}` : '') +
    `.\n\nMerci de nous revenir en répondant à ce courriel.\n\n${auteur}\nIsolation Larente`
  const fichier = pdf ? new File([pdf.blob], pdf.nomFichier, { type: 'application/pdf' }) : null
  const partageable = !!fichier && typeof navigator.canShare === 'function' && navigator.canShare({ files: [fichier] })
  const pasEnvoyee = suivi.numero == null

  async function preparer() {
    setOccupe(true)
    setErreur(null)
    try {
      setPdf(await fichePdf(suivi, chantier, auteur))
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setOccupe(false)
    }
  }

  async function partager() {
    if (!fichier) return
    try {
      await navigator.share({ files: [fichier], title: sujet, text: corps })
      await envoye('partage depuis l\'appareil')
      setFait('Fiche partagée. Vérifie que le courriel est bien parti.')
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return
      setErreur(e instanceof Error ? e.message : String(e))
    }
  }

  function telecharger() {
    if (!pdf) return
    const url = URL.createObjectURL(pdf.blob)
    const a = document.createElement('a')
    a.href = url
    a.download = pdf.nomFichier
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }

  async function courriel() {
    telecharger()
    const a = suivi.assigne_contact_courriel ?? ''
    window.location.href = `mailto:${encodeURIComponent(a)}?subject=${encodeURIComponent(sujet)}&body=${encodeURIComponent(corps)}`
    await envoye('courriel préparé, PDF téléchargé')
    setFait(`Le PDF est dans tes téléchargements (${pdf?.nomFichier}). Glisse-le dans le courriel qui vient de s'ouvrir avant d'envoyer.`)
  }

  return (
    <div className="annot-voile" onClick={annuler}>
      <div className="annot-dialogue" onClick={(e) => e.stopPropagation()}>
        <p className="titre">Envoyer {code} à {suivi.assigne_contact_nom}</p>
        {!suivi.assigne_contact_courriel && (
          <p className="avis" style={{ fontSize: 13 }}>
            Ce contact n'a pas de courriel dans l'onglet Info. Tu pourras choisir le destinataire au moment d'envoyer.
          </p>
        )}
        {pasEnvoyee ? (
          <p style={{ fontSize: 13.5 }}>
            La fiche n'a pas encore son numéro : elle doit d'abord partir au serveur (réseau requis). Réessaie dans un instant.
          </p>
        ) : fait ? (
          <p style={{ fontSize: 13.5 }}>{fait}</p>
        ) : !pdf ? (
          <p style={{ fontSize: 13.5 }}>
            Le PDF contient la description, l'extrait du plan avec l'épingle et les photos. Les commentaires internes n'y sont pas.
          </p>
        ) : (
          <>
            <p style={{ fontSize: 13.5 }}>
              PDF prêt : <strong>{pdf.nomFichier}</strong> ({Math.max(1, Math.round(pdf.blob.size / 1024))} Ko).
            </p>
            {pdf.manques.length > 0 && (
              <p className="avis" style={{ fontSize: 13 }}>
                Non inclus : {pdf.manques.join(', ')}.
              </p>
            )}
          </>
        )}
        {erreur && <p className="avis erreur">{erreur}</p>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="bouton creux" onClick={annuler}>
            {fait ? 'Fermer' : 'Annuler'}
          </button>
          {!pasEnvoyee && !fait && !pdf && (
            <button className="bouton" disabled={occupe} onClick={() => void preparer()}>
              {occupe ? 'Préparation…' : 'Préparer le PDF'}
            </button>
          )}
          {pdf && !fait && partageable && (
            <button className="bouton" onClick={() => void partager()}>
              Partager (Gmail, Outlook…)
            </button>
          )}
          {pdf && !fait && (
            <button className={`bouton ${partageable ? 'creux' : ''}`} onClick={() => void courriel()}>
              Télécharger + ouvrir un courriel
            </button>
          )}
          {pdf && !fait && (
            <button className="bouton creux" onClick={telecharger}>
              Voir le PDF
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------------
   Photos d'un suivi
   --------------------------------------------------------------------- */

/** Enregistre des images comme photos du chantier (file d'attente) et renvoie leurs identifiants. */
async function enregistrerPhotos(chantierId: string, blobs: Blob[], depuisAppareil: boolean): Promise<string[]> {
  const uuids: string[] = []
  for (const brut of blobs) {
    const { blob, largeur, hauteur } = await preparerImage(brut)
    const exif = depuisAppareil ? null : await dateExif(brut)
    const uuid = crypto.randomUUID()
    await mettreEnAttente({
      client_uuid: uuid,
      chantier_id: chantierId,
      prise_le: (exif ?? new Date()).toISOString(),
      source: depuisAppareil ? 'appareil' : 'televersee',
      origine_date: depuisAppareil ? 'appareil' : exif ? 'exif' : 'inconnue',
      note: null,
      blob,
      largeur,
      hauteur,
      cree_le: Date.now(),
      essais: 0
    })
    uuids.push(uuid)
  }
  return uuids
}

function PhotosSuivi({
  chantier,
  photos,
  modifiable,
  ajouter,
  retirer
}: {
  chantier: Chantier
  photos: PhotoSuivi[]
  modifiable: boolean
  ajouter: (uuids: string[]) => void
  retirer: (uuid: string) => void
}) {
  const [urls, setUrls] = useState<Record<string, string>>({})
  const [camera, setCamera] = useState(false)
  useHistorique(camera, setCamera)
  const [ouverte, setOuverte] = useState<number | null>(null)
  const [occupe, setOccupe] = useState(false)
  const champ = useRef<HTMLInputElement>(null)
  const cle = photos.map((p) => p.photo_client_uuid).join(',')

  useEffect(() => {
    let vivant = true
    const locales: string[] = []
    void (async () => {
      const uuids = cle ? cle.split(',') : []
      const carte: Record<string, string> = {}
      // Photos encore sur l'appareil (pas envoyées).
      for (const p of await fileDAttente(chantier.id)) {
        if (uuids.includes(p.client_uuid)) {
          const u = URL.createObjectURL(p.blob)
          locales.push(u)
          carte[p.client_uuid] = u
        }
      }
      const restantes = uuids.filter((u) => !carte[u])
      if (restantes.length > 0 && navigator.onLine) {
        const { data } = await supabase.from('photos').select('client_uuid, fichier_path').in('client_uuid', restantes)
        const lignes = (data ?? []) as { client_uuid: string; fichier_path: string }[]
        if (lignes.length > 0) {
          const { data: signees } = await supabase.storage
            .from('photos')
            .createSignedUrls(lignes.map((l) => l.fichier_path), 3600)
          const parChemin = new Map((signees ?? []).map((s) => [s.path, s.signedUrl]))
          for (const l of lignes) {
            const u = parChemin.get(l.fichier_path)
            if (u) carte[l.client_uuid] = u
          }
        }
      }
      if (vivant) setUrls(carte)
    })()
    return () => {
      vivant = false
      locales.forEach((u) => URL.revokeObjectURL(u))
    }
  }, [cle, chantier.id])

  async function recevoir(blobs: Blob[], depuisAppareil: boolean) {
    setOccupe(true)
    try {
      const uuids = await enregistrerPhotos(chantier.id, blobs, depuisAppareil)
      ajouter(uuids)
      void synchroniser()
    } finally {
      setOccupe(false)
    }
  }

  const liste = photos.map((p) => p.photo_client_uuid)
  const urlOuverte = ouverte != null ? urls[liste[ouverte]] : null

  return (
    <>
      <div className="sect">Photos {liste.length > 0 && <span className="code">{liste.length}</span>}</div>
      {liste.length > 0 && (
        <div className="grille" style={{ marginBottom: 10 }}>
          {liste.map((u, i) => (
            <div key={u} className="tuile">
              <button style={{ display: 'block', width: '100%', height: '100%', padding: 0 }} onClick={() => setOuverte(i)}>
                {urls[u] ? <img src={urls[u]} alt="" loading="lazy" /> : <span className="code" style={{ padding: 6, display: 'block' }}>{navigator.onLine ? '…' : 'hors ligne'}</span>}
              </button>
              {modifiable && (
                <button className="tuile-retirer" onClick={() => retirer(u)} aria-label="Retirer la photo du suivi" title="Retirer du suivi (la photo reste dans l'onglet Photos)">
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {modifiable && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="bouton creux" disabled={occupe} onClick={() => setCamera(true)}>
            Appareil photo
          </button>
          <button className="bouton creux" disabled={occupe} onClick={() => champ.current?.click()}>
            Téléverser
          </button>
        </div>
      )}
      <input
        ref={champ}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          const f = e.target.files
          if (f && f.length > 0) void recevoir(Array.from(f), false)
          e.target.value = ''
        }}
      />
      {camera && (
        <CameraRafale
          fermer={() => setCamera(false)}
          capturer={(blobs) => {
            setCamera(false)
            void recevoir(blobs, true)
          }}
        />
      )}
      {urlOuverte && ouverte != null && (
        <VisionneusePhoto
          url={urlOuverte}
          fermer={() => setOuverte(null)}
          position={{ rang: ouverte + 1, total: liste.length }}
          precedente={ouverte > 0 ? () => setOuverte(ouverte - 1) : undefined}
          suivante={ouverte < liste.length - 1 ? () => setOuverte(ouverte + 1) : undefined}
        />
      )}
    </>
  )
}
